/**
 * Converts text formatting commands to HTML
 */
export function formatTextToHtml(text: string): string {
  // Define known label patterns that should get special formatting (all languages)
  const labelPatterns = [
    // German
    'Verarbeitete Datenarten:',
    'Betroffene Personen:',
    'Zwecke der Verarbeitung:',
    'Aufbewahrung und Löschung:',
    'Rechtsgrundlagen:',
    'Weitere Hinweise zu Verarbeitungsprozessen, Verfahren und Diensten:',
    'Löschung von Daten:',
    'Grundlage Drittlandtransfers:',
    'Dienstanbieter:',
    'Website:',
    'Datenschutzerklärung:',
    'Auftragsverarbeitungsvertrag:',
    // English
    'Data types processed:',
    'Data subjects:',
    'Purposes of processing:',
    'Retention and deletion:',
    'Legal bases:',
    'Further information on processing operations, procedures, and services:',
    'Deletion of data:',
    'Basis for third country transfer:',
    'Service provider:',
    'Privacy policy:',
    'Data processing agreement:',
    // French
    'Types de données traitées :',
    'Personnes concernées :',
    'Finalités du traitement :',
    'Conservation et suppression :',
    'Bases juridiques :',
    'Informations supplémentaires sur les opérations de traitement, procédures et services :',
    'Suppression des données :',
    'Base pour le transfert vers des pays tiers :',
    'Prestataire de services :',
    'Politique de confidentialité :',
    'Accord de traitement des données :',
    // Spanish
    'Tipos de datos procesados:',
    'Personas afectadas:',
    'Fines del procesamiento:',
    'Retención y eliminación:',
    'Bases legales:',
    'Más información sobre operaciones de procesamiento, procedimientos y servicios:',
    'Eliminación de datos:',
    'Base para transferencia a terceros países:',
    'Proveedor de servicios:',
    'Política de privacidad:',
    'Acuerdo de procesamiento de datos:',
  ];

  // Sub-service patterns (entries after "Weitere Hinweise..." / "Further information...")
  const subServicePattern = /^([A-Za-zÀ-ÿÄÖÜäöüß][A-Za-zÀ-ÿÄÖÜäöüß0-9\s,\-()]+):\s/;

  // Split by double newlines first to get paragraphs
  const paragraphs = text.split('\n\n');
  
  // "Further information" header patterns for all languages
  const furtherInfoPatterns = [
    'Weitere Hinweise',                    // German
    'Further information',                  // English
    'Informations supplémentaires',         // French
    'Más información',                      // Spanish
  ];

  // Legal bases / service provider patterns for all languages (for sub-service detection)
  const legalBasesOrProviderPatterns = [
    'Rechtsgrundlagen:', 'Dienstanbieter:',                     // German
    'Legal bases:', 'Service provider:',                         // English
    'Bases juridiques :', 'Prestataire de services :',           // French
    'Bases legales:', 'Proveedor de servicios:',                 // Spanish
  ];

  const processedParagraphs = paragraphs.map(paragraph => {
    // Check if this is a "Further information..." header (any language)
    const isFurtherInfoHeader = paragraph.trim().endsWith(':') && 
      furtherInfoPatterns.some(pattern => paragraph.includes(pattern));
    
    if (isFurtherInfoHeader) {
      return `<p class="font-semibold mt-6 mb-2">${paragraph.trim()}</p>`;
    }

    // Check if paragraph starts with a known label pattern
    const startsWithLabel = labelPatterns.some(label => paragraph.startsWith(label));
    
    // Check if this looks like a labeled entry (for service providers, etc.)
    const isLabeledEntry = subServicePattern.test(paragraph.trim()) && 
      legalBasesOrProviderPatterns.some(pattern => paragraph.includes(pattern));

    if (startsWithLabel || isLabeledEntry) {
      // Find the label and content
      const colonIndex = paragraph.indexOf(':');
      if (colonIndex > 0) {
        const label = paragraph.substring(0, colonIndex + 1);
        const content = paragraph.substring(colonIndex + 1).trim();
        
        // Check if content contains bullet points - if so, render as list
        if (content.includes('• ')) {
          const bulletItems = content.split('• ').filter(item => item.trim());
          const listHtml = bulletItems.map(item => 
            `<li>${item.trim().replace(/\.$/, '')}</li>`
          ).join('');
          
          return `<div class="flex gap-2 my-3">
            <span class="text-primary flex-shrink-0 mt-1">›</span>
            <div>
              <strong>${label}</strong>
              <ul class="list-disc ml-6 my-2">${listHtml}</ul>
            </div>
          </div>`;
        }
        
        // Process content - handle semicolons as separators for lists
        const processedContent = content
          .replace(/\n/g, '<br>')
          .replace(/; /g, '; ');
        
        return `<div class="flex gap-2 my-3">
          <span class="text-primary flex-shrink-0 mt-1">›</span>
          <div><strong>${label}</strong> ${processedContent}</div>
        </div>`;
      }
    }

    // Regular paragraph processing
    return '<p>' + paragraph
      .replace(/\n/g, '<br>')
      .replace(/• /g, '</p><ul><li>')
      .replace(/<\/li>(<br>)?• /g, '</li><li>')
      + '</p>';
  });

  let result = processedParagraphs.join('');
  
  // Clean up list handling
  result = result.replace(/<p><\/p>/g, '');
  result = result.replace(/<ul><li>(.*?)(<\/p>|$)/gs, (match, content, ending) => {
    // Split by line breaks to get individual list items
    const items = content.split(/<br>|<li>/).filter((item: string) => item.trim());
    if (items.length > 0) {
      return '<ul class="list-disc ml-6 my-2">' + 
        items.map((item: string) => `<li>${item.trim()}</li>`).join('') + 
        '</ul>' + (ending === '</p>' ? '' : '');
    }
    return match;
  });

  return result;
}
